import {
  createFileRoute,
  redirect,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import {
  GetPeriodosGarantidosRecorrentesDoUsuarioDocument,
  ReservedScheduleCancelPageQueryDocument,
} from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import { CpsSpinner } from "corpus";

import { ReservedScheduleCancelFailDrawer } from "@/components/reserved-schedules/reserved-schedule-cancel-fail-drawer";
import { ReservedScheduleCancelSimulationInfo } from "@/components/reserved-schedules/reserved-schedule-cancel-simulation-info";
import { ReservedScheduleCancelSuccessDrawer } from "@/components/reserved-schedules/reserved-schedule-cancel-success-drawer";
import { ReservedScheduleTermsOfUseUrl } from "@/components/reserved-schedules/reserved-schedule-terms-of-use-url";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Button } from "@/components/ui/button/button";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  ensureQueryData,
  useGraphQL,
  useGraphQLMutation,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { DaysOfWeekToPortugueseAdverbial } from "@/lib/enums/days-of-week-enum";
import { formatWithZonedDate } from "@/lib/helpers/date";
import { formatTimeHourMinute, parseTime } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

import type { JSX } from "react";

const reservedScheduleCancelPageSearchParamsSchema = z.object({
  success: z.boolean().optional(),
  fail: z.boolean().optional(),
});

const ReservedScheduleCancelPageQuery = graphql(/* GraphQL */ `
  query ReservedScheduleCancelPageQuery(
    $codPeriodoGarantidoRecorrente: Int!
    $dataAgendamento: date!
    $horaInicio: time!
  ) {
    periodoGarantidoRecorrente: tbPeriodosGarantidosRecorrentes_by_pk(
      codPeriodoGarantidoRecorrente: $codPeriodoGarantidoRecorrente
    ) {
      ativo
      codUsuarioAgenda
      dataInicio
      horaInicio
      horaFim
      diaDaSemana
      Unidade {
        nomeLimpo
        codUnidade
      }
      TipoSala {
        nome
      }
      PeriodosGarantidos(
        where: {
          Agendamento: {
            cancelado: { _eq: false }
            _or: [
              { data: { _gt: $dataAgendamento } }
              {
                _and: [
                  { data: { _eq: $dataAgendamento } }
                  { horaInicio: { _gte: $horaInicio } }
                ]
              }
            ]
          }
          ativo: { _eq: true }
        }
      ) {
        codPeriodoGarantido
        codModalidadePeriodoGarantido
        Agendamento {
          data
        }
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const ReservedScheduleCancelSimulationQuery = graphql(/* GraphQL */ `
  query ReservedScheduleCancelSimulationQuery($codPeriodoGarantido: Int!) {
    LivanceApiSimulaCobrancaDeCancelamento(
      arg1: { codPeriodoGarantido: $codPeriodoGarantido, dataReferencia: null }
    ) {
      taxa
      valor
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const CancelReservedScheduleMutation = graphql(/* GraphQL */ `
  mutation CancelReservedScheduleMutation($codPeriodoGarantidoRecorrente: Int!) {
    LivanceApiCancelaPeriodoGarantidoRecorrente(
      periodoGarantidoRecorrente: {
        codPeriodoGarantidoRecorrente: $codPeriodoGarantidoRecorrente
      }
    ) {
      message
      success
    }
  }
`);

const ReservedScheduleCancelPage = (): JSX.Element => {
  const router = useRouter();
  const { reservedScheduleId } = Route.useParams();

  const { user } = useRouteContext({ from: "__root__" });

  const params = useSearch({
    from: "/settings/reserved-schedules/$reservedScheduleId/cancel",
  });
  const queryResult = useGraphQL(ReservedScheduleCancelPageQuery, {
    codPeriodoGarantidoRecorrente: reservedScheduleId,
    dataAgendamento: formatWithZonedDate(new Date(), "yyyy-MM-dd"),
    horaInicio: formatWithZonedDate(new Date(), "HH:mm"),
  });

  const { data } = queryResult;
  const reservedSchedule = data?.periodoGarantidoRecorrente;
  const nextSchedule = reservedSchedule?.PeriodosGarantidos[0];
  // Agenda aberta junto com o PG (KRA-447): cancelar o PGR também a encerra (KRA-453).
  const hasLinkedSchedule = typeof reservedSchedule?.codUsuarioAgenda === "number";

  const queryResultCancelSimulation = useGraphQL(
    ReservedScheduleCancelSimulationQuery,
    { codPeriodoGarantido: nextSchedule?.codPeriodoGarantido ?? 0 },
    { enabled: Boolean(nextSchedule?.codPeriodoGarantido) },
  );

  const { data: dataCancelSimulation } = queryResultCancelSimulation;
  const simulationTax =
    dataCancelSimulation?.LivanceApiSimulaCobrancaDeCancelamento?.taxa ?? 0;
  const simulationCost =
    dataCancelSimulation?.LivanceApiSimulaCobrancaDeCancelamento?.valor ?? 0;

  const { mutateAsync, isPending } = useGraphQLMutation(CancelReservedScheduleMutation);

  const invalidateReservedScheduleListQuery = useInvalidateQuery(
    GetPeriodosGarantidosRecorrentesDoUsuarioDocument,
  );
  const invalidateReservedScheduleCancelPageQuery = useInvalidateQuery(
    ReservedScheduleCancelPageQueryDocument,
  );

  const handleReservedScheduleCancelAsync = async (): Promise<void> => {
    const variables = {
      codPeriodoGarantidoRecorrente: reservedScheduleId,
    };

    const onSuccess = (): void => {
      trackEvent("Periodo Garantido Recorrente Cancelado", {
        codUsuario: user.codUsuario,
        codPeriodoGarantidoRecorrente: reservedScheduleId,
        codUnidade: reservedSchedule?.Unidade.codUnidade,
      });

      void router.navigate({
        to: ".",
        search: {
          success: true,
        },
      });

      invalidateReservedScheduleListQuery();
      invalidateReservedScheduleCancelPageQuery();
    };

    const onError = (error: Error): void => {
      logError(error, variables);
      void router.navigate({
        to: ".",
        search: {
          fail: true,
        },
      });
    };

    await mutateAsync(variables, { onSuccess, onError });
  };

  const setOpenDrawer = (open: boolean, paramName: "success" | "fail"): void => {
    const search = {
      success: undefined,
      fail: undefined,
      ...params,
    };
    if (open) {
      search[paramName] = open;

      void router.navigate({
        to: ".",
        search,
      });
    } else if (params[paramName]) {
      router.history.back();
    }
  };

  const dayOfWeek = DaysOfWeekToPortugueseAdverbial.get(
    reservedSchedule?.diaDaSemana ?? 0,
  )?.toLocaleLowerCase();
  const startTime = formatTimeHourMinute(parseTime(reservedSchedule?.horaInicio ?? ""));
  const endTime = formatTimeHourMinute(parseTime(reservedSchedule?.horaFim ?? ""));
  const roomTypeName = reservedSchedule?.TipoSala.nome;
  const locationName = reservedSchedule?.Unidade.nomeLimpo;

  const successDrawerDescription = `O seu período garantido de ${locationName}, em sala ${roomTypeName}, ${dayOfWeek}, das ${startTime} às ${endTime}, foi cancelado.`;

  const navigateToCalendar = (): void => {
    router.history.destroy();
    void router.navigate({
      to: "/calendar",
    });
  };

  const navigateToScheduleList = (): void => {
    router.history.go(-3);
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          <div className="flex flex-col gap-6">
            <p className="font-medium text-lg">Cancelar período garantido</p>
            <p>
              Ao cancelar o período garantido, você perderá a garantida de disponibilidade
              de sala.
            </p>
            {hasLinkedSchedule ? (
              // TODO copy
              <p>
                A agenda vinculada a este período também será encerrada, e os horários
                dela deixam de ficar disponíveis para agendamento.
              </p>
            ) : null}
            <AsyncDataWrapper {...queryResultCancelSimulation} fallback={<CpsSpinner />}>
              <ReservedScheduleCancelSimulationInfo
                tax={simulationTax}
                cost={simulationCost}
                locationName={reservedSchedule?.Unidade.nomeLimpo}
                dayOfWeek={reservedSchedule?.diaDaSemana}
                date={nextSchedule?.Agendamento.data}
                startTime={reservedSchedule?.horaInicio}
                endTime={reservedSchedule?.horaFim}
              />
            </AsyncDataWrapper>
            <p>
              Mais detalhes em{" "}
              <ReservedScheduleTermsOfUseUrl
                reservedScheduleType={nextSchedule?.codModalidadePeriodoGarantido}
                title="Termos e Condições de Uso"
              />
              .
            </p>
            <Button
              data-testid="botao-cancela-periodo-garantido"
              onClick={() => {
                void handleReservedScheduleCancelAsync();
              }}
              loading={isPending}
            >
              Cancelar período garantido
            </Button>
          </div>
          <ReservedScheduleCancelSuccessDrawer
            description={successDrawerDescription}
            open={Boolean(params.success)}
            primaryButtonText="Ir para meus períodos"
            secondaryButtonText="Ir para agenda"
            primaryButtonAction={navigateToScheduleList}
            secondaryButtonAction={navigateToCalendar}
          />
          <ReservedScheduleCancelFailDrawer
            open={Boolean(params.fail)}
            setOpen={(open: boolean) => {
              setOpenDrawer(open, "fail");
            }}
          />
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/reserved-schedules/$reservedScheduleId/cancel",
)({
  component: ReservedScheduleCancelPage,
  validateSearch: reservedScheduleCancelPageSearchParamsSchema,
  parseParams: (params) => ({
    reservedScheduleId: z.int().parse(Number(params.reservedScheduleId)),
  }),
  beforeLoad: async (opts) => {
    try {
      const codPeriodoGarantidoRecorrente = Number(opts.params.reservedScheduleId);
      const dataAgendamento = formatWithZonedDate(new Date(), "yyyy-MM-dd");
      const horaInicio = formatWithZonedDate(new Date(), "HH:mm");

      const data = await ensureQueryData(opts.context, ReservedScheduleCancelPageQuery, {
        codPeriodoGarantidoRecorrente,
        dataAgendamento,
        horaInicio,
      });

      if (!data.periodoGarantidoRecorrente?.ativo) {
        throw new Error("reserved schedule is not active");
      }
    } catch (_e) {
      redirect({
        to: "/settings/reserved-schedules",
        throw: true,
      });
    }
  },
});
